import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'ui/modal';
import { InputField, RadioGroupField } from 'lib/iterable-hook-form';
import { PrimaryButton } from 'ui/button';
import { Checkbox } from 'ui/checkboxV2';
import { Orientation } from 'ui/radioGroup/types';
import { FormField } from 'ui/formField';
import { ORG_PERMISSIONS } from 'utils/permissions';
import {
  MemberFormSchema,
  FIRST_NAME,
  LAST_NAME,
  EMAIL,
  ORG_ROLE,
  OrgRole,
  ORGANIZATION_PERMISSIONS,
} from '../../contexts/MemberFormProvider';
import { Row, FormFieldWithoutMargin, CheckboxRow, CheckboxRows } from './MemberModal.styled';
import { useOrganizationModalsContext } from '../../contexts/OrganizationModalsContext';

const ORG_PERMISSION_OPTIONS = [
  {
    name: 'createProjects',
    label: 'Create projects',
    value: ORG_PERMISSIONS.CreateProjects,
  },
  {
    name: 'manageBilling',
    label: 'Manage billing',
    value: ORG_PERMISSIONS.ManageBilling,
  },
  {
    name: 'manageMembers',
    label: 'Manage members',
    value: ORG_PERMISSIONS.ManageMembers,
  },
  {
    name: 'manageRoles',
    label: 'Manage roles',
    value: ORG_PERMISSIONS.ManageRoles,
  },
];

export const MemberModal = ({
  onSubmit,
  id,
  headerText,
  confirmBtnText,
  isLoading,
  disableEmailInput = false,
  includeRoleAndPermissionsFields = false,
}: {
  id: string;
  headerText: string;
  confirmBtnText: string;
  isLoading: boolean;
  onSubmit: (values: MemberFormSchema) => void;
  disableEmailInput?: boolean;
  includeRoleAndPermissionsFields?: boolean;
}) => {
  const { hideModal } = useOrganizationModalsContext();
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useFormContext<MemberFormSchema>();

  const orgRole = watch(ORG_ROLE);
  const orgPermissions = watch(ORGANIZATION_PERMISSIONS);

  useEffect(() => {
    if (orgRole === OrgRole.ADMIN) {
      setValue(ORGANIZATION_PERMISSIONS, [
        ORG_PERMISSIONS.CreateProjects,
        ORG_PERMISSIONS.ManageBilling,
        ORG_PERMISSIONS.ManageMembers,
        ORG_PERMISSIONS.ManageRoles,
      ]);
    }
  }, [orgRole, setValue]);

  const onCheckPermission = (value: number) => (checked: boolean | 'indeterminate') => {
    const next = checked
      ? [...orgPermissions, value]
      : orgPermissions.filter((permission: number) => permission !== value);
    setValue(ORGANIZATION_PERMISSIONS, next);
  };

  return (
    <Modal id={id} onClose={hideModal} isModalOpen={true}>
      <ModalHeader>{headerText}</ModalHeader>
      <ModalBody>
        <Row>
          <FormField
            labelText="First name"
            inputId={FIRST_NAME}
            hasError={!!errors[FIRST_NAME]}
            errorText={errors[FIRST_NAME]?.message as string}
          >
            <InputField id={FIRST_NAME} name={FIRST_NAME} />
          </FormField>
          <FormFieldWithoutMargin
            labelText="Last name"
            inputId={LAST_NAME}
            hasError={!!errors[LAST_NAME]}
            errorText={errors[LAST_NAME]?.message as string}
          >
            <InputField id={LAST_NAME} name={LAST_NAME} />
          </FormFieldWithoutMargin>
        </Row>
        <FormField
          labelText="Email"
          inputId={EMAIL}
          hasError={!!errors[EMAIL]}
          errorText={errors[EMAIL]?.message as string}
          isDisabled={disableEmailInput}
        >
          <InputField id={EMAIL} name={EMAIL} isDisabled={disableEmailInput} />
        </FormField>
        {includeRoleAndPermissionsFields && (
          <>
            <FormField labelText="Org role">
              <RadioGroupField
                name={ORG_ROLE}
                orientation={Orientation.HORIZONTAL}
                isBordered
                options={[
                  {
                    id: 'admin',
                    label: 'Admin',
                    value: OrgRole.ADMIN,
                    description:
                      'Org admins have access to all organization permissions and projects.',
                  },
                  {
                    id: 'members',
                    label: 'Members',
                    value: OrgRole.MEMBER,
                    description: 'Org members have access to select org permissions and projects.',
                  },
                ]}
              />
            </FormField>
            <FormField labelText="Org permissions">
              <CheckboxRows>
                {ORG_PERMISSION_OPTIONS.map(({ name, label, value }) => (
                  <CheckboxRow key={name}>
                    <Checkbox
                      setChecked={onCheckPermission(value)}
                      name={name}
                      label={label}
                      status={orgPermissions?.includes(value)}
                      isDisabled={orgRole === OrgRole.ADMIN}
                    />
                  </CheckboxRow>
                ))}
              </CheckboxRows>
            </FormField>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={handleSubmit(onSubmit)}
          isDisabled={!isValid || isLoading}
          isLoading={isLoading}
        >
          {confirmBtnText}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
